import React, { useState } from 'react';
import { Bot, MessageSquare, Edit, Target, Search, Zap, Brain, Cpu, Clock, ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const IntelligenceArtificiellePage = ({ setCurrentSection, onSelectGuide, guides }) => {
  // Organiser les guides par types d'IA
  const toolCategories = {
    conversational: {
      name: 'IA Conversationnelles',
      icon: MessageSquare,
      description: 'ChatGPT, Claude, Gemini et assistants virtuels',
      gradient: 'from-blue-500 to-indigo-600',
      guides: []
    },
    creative: {
      name: 'IA Créatives',
      icon: Edit,
      description: 'Génération d\'images, vidéos et contenu artistique',
      gradient: 'from-pink-500 to-purple-600',
      guides: []
    },
    specialized: {
      name: 'IA Spécialisées',
      icon: Target,
      description: 'Outils IA pour secteurs spécifiques',
      gradient: 'from-green-500 to-teal-600',
      guides: []
    },
    productivity: {
      name: 'IA Productivité',
      icon: Zap,
      description: 'Automatisation et optimisation du travail',
      gradient: 'from-orange-500 to-red-600',
      guides: []
    },
    research: {
      name: 'IA Recherche',
      icon: Search,
      description: 'Moteurs de recherche et analyse intelligente',
      gradient: 'from-purple-500 to-indigo-600',
      guides: []
    },
    coding: {
      name: 'IA Programmation',
      icon: Cpu,
      description: 'Assistance au développement et génération de code',
      gradient: 'from-cyan-500 to-blue-600',
      guides: []
    }
  };

  // Function to count procedures in a guide
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Bot className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Intelligence Artificielle
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto">
              Découvrez et maîtrisez les outils d\'IA qui révolutionnent le travail et la créativité
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-indigo-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-purple-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-pink-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* IA Tools Section */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-500 rounded-2xl mb-6">
              <Brain className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Catégories d\'Intelligence Artificielle
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explorez les différents types d\'IA selon vos besoins professionnels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(toolCategories).map(([key, category]) => {
              const IconComponent = category.icon;
              const hasGuides = category.guides.length > 0;
              const firstGuideId = hasGuides ? category.guides[0] : null;
              const procedureCount = hasGuides ? countProceduresInGuide(firstGuideId) : 0;
              
              return (
                <Card 
                  key={key} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (hasGuides && onSelectGuide && firstGuideId) {
                      console.log('IA Guide selected:', { id: firstGuideId });
                      onSelectGuide({ id: firstGuideId });
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {category.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {category.description}
                    </CardDescription>
                    
                    {hasGuides && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {hasGuides && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${hasGuides ? `group-hover:bg-gradient-to-r group-hover:${category.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!hasGuides}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        hasGuides 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {hasGuides ? 'Consulter le guide' : 'Bientôt disponible'}
                      {hasGuides && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default IntelligenceArtificiellePage;